import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../App";
import {
  LayoutDashboard, Users, FileText, ListOrdered, MapPin, BookOpen, Layers,
  GraduationCap, Image as ImageIcon, BookOpenCheck, MessageSquare, Heart,
  HelpCircle, UserPlus, Upload, ClipboardList, Shield, Trash2, ChevronDown,
  ChevronRight, Menu, X, Search, BookMarked, BarChart3, FileSignature, HardHat, Network,
} from "lucide-react";

/** Single source-of-truth for admin nav. Each item carries a `roles` filter
 *  so we can hide entire chunks from instructors. */
export const ADMIN_NAV_SECTIONS = [
  {
    id: "overview-section",
    label: null, // No header for overview
    items: [
      { value: "overview", label: "Overview", icon: LayoutDashboard, roles: ["admin", "instructor"] },
    ],
  },
  {
    id: "people",
    label: "People",
    items: [
      { value: "pipeline", label: "Pipeline", icon: ListOrdered, roles: ["admin", "instructor"] },
      { value: "enrollees", label: "Enrollees", icon: Users, roles: ["admin"] },
      { value: "users", label: "Users", icon: UserPlus, roles: ["admin"] },
      { value: "mentors", label: "Mentor Apps", icon: Heart, roles: ["admin"] },
    ],
  },
  {
    id: "waitlists",
    label: "Waitlists",
    items: [
      { value: "waitlist", label: "Program Waitlist", icon: ClipboardList, roles: ["admin"] },
      { value: "state-waitlist", label: "State Demand", icon: MapPin, roles: ["admin"] },
    ],
  },
  {
    id: "reporting",
    label: "Reporting",
    items: [
      { value: "reports", label: "Reports & Export", icon: BarChart3, roles: ["admin"] },
      { value: "esignatures", label: "E-Signatures", icon: FileSignature, roles: ["admin"] },
    ],
  },
  {
    id: "academic",
    label: "Academic",
    items: [
      { value: "programs", label: "Programs", icon: GraduationCap, roles: ["admin"] },
      { value: "cohorts", label: "Cohorts", icon: Layers, roles: ["admin", "instructor"] },
      { value: "courses", label: "Materials", icon: BookOpen, roles: ["admin", "instructor"] },
      { value: "instructor-library", label: "Instructor Library", icon: BookMarked, roles: ["admin", "instructor"] },
    ],
  },
  {
    id: "compliance",
    label: "Compliance",
    items: [
      { value: "apprenticeship", label: "Apprenticeship", icon: HardHat, roles: ["admin"] },
    ],
  },
  {
    id: "resources",
    label: "Resources",
    items: [
      { value: "architecture", label: "Architecture", icon: Network, roles: ["admin"] },
    ],
  },
  {
    id: "content",
    label: "Content",
    items: [
      { value: "site-content", label: "Homepage CMS", icon: ImageIcon, roles: ["admin"] },
      { value: "welcome-pack", label: "Welcome Packets", icon: BookOpenCheck, roles: ["admin", "instructor"] },
      { value: "contact-page", label: "Contact Page", icon: MessageSquare, roles: ["admin"] },
      { value: "stories", label: "Stories", icon: FileText, roles: ["admin"] },
      { value: "faqs", label: "FAQs", icon: HelpCircle, roles: ["admin"] },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    items: [
      { value: "bulk-import", label: "Bulk Import", icon: Upload, roles: ["admin"] },
      { value: "working-style", label: "Working-Style Quiz", icon: ClipboardList, roles: ["admin", "instructor"] },
      { value: "security", label: "Security", icon: Shield, roles: ["admin"] },
      { value: "maintenance", label: "Maintenance", icon: Trash2, roles: ["admin"], danger: true },
    ],
  },
];

const AdminSidebar = ({ active, onChange }) => {
  const { user } = useAuth();
  const role = user?.role || "admin";
  const [collapsedSections, setCollapsedSections] = useState({});
  const [search, setSearch] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Restore sidebar state from localStorage (per-admin preference)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("admin-sidebar-collapsed");
      if (raw) setCollapsedSections(JSON.parse(raw));
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("admin-sidebar-collapsed", JSON.stringify(collapsedSections));
    } catch (_) { /* ignore */ }
  }, [collapsedSections]);

  const toggleSection = (id) => {
    setCollapsedSections((c) => ({ ...c, [id]: !c[id] }));
  };

  // Filter nav by role + search
  const filteredSections = useMemo(() => {
    const term = search.trim().toLowerCase();
    return ADMIN_NAV_SECTIONS.map((section) => {
      const items = section.items.filter((it) => {
        if (!it.roles.includes(role)) return false;
        if (term && !it.label.toLowerCase().includes(term)) return false;
        return true;
      });
      return { ...section, items };
    }).filter((s) => s.items.length > 0);
  }, [role, search]);

  const handleSelect = (value) => {
    onChange(value);
    setMobileOpen(false);
  };

  const renderNavList = () => (
    <>
      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search admin…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white border border-slate-200 focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10 focus:outline-none"
          data-testid="admin-sidebar-search"
        />
      </div>

      {filteredSections.map((section) => {
        const collapsed = collapsedSections[section.id];
        return (
          <div key={section.id} className="mb-4">
            {section.label && (
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between px-2 mb-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 hover:text-slate-600 transition"
                data-testid={`admin-section-toggle-${section.id}`}
              >
                <span>{section.label}</span>
                {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            )}
            {!collapsed && (
              <ul className="space-y-0.5">
                {section.items.map((it) => {
                  const Icon = it.icon;
                  const isActive = active === it.value;
                  const danger = it.danger;
                  return (
                    <li key={it.value}>
                      <button
                        onClick={() => handleSelect(it.value)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition group
                          ${isActive
                            ? danger
                              ? "bg-rose-700 text-white shadow-sm"
                              : "bg-brand-navy text-white shadow-sm"
                            : "text-slate-700 hover:bg-slate-100"}
                          ${danger && !isActive ? "text-rose-700" : ""}
                        `}
                        data-testid={`admin-nav-${it.value}`}
                        aria-current={isActive ? "page" : undefined}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${isActive ? "" : danger ? "text-rose-700" : "text-slate-500 group-hover:text-brand-navy"}`} />
                        <span className="truncate">{it.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}

      {filteredSections.length === 0 && (
        <p className="text-sm text-slate-500 italic px-3 py-4">No items match your search.</p>
      )}
    </>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 mb-4"
        data-testid="admin-sidebar-mobile-toggle"
      >
        <Menu className="w-4 h-4" /> Admin Menu
      </button>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col w-60 shrink-0 sticky top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto pr-2 pb-6"
        data-testid="admin-sidebar"
        aria-label="Admin navigation"
      >
        {renderNavList()}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="admin-sidebar-mobile-drawer">
          <div className="bg-black/40 absolute inset-0" onClick={() => setMobileOpen(false)} />
          <div className="relative bg-white w-72 max-w-[85vw] h-full p-4 overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold uppercase tracking-wider text-slate-700">Admin</span>
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            {renderNavList()}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminSidebar;
